<!--
  One item for sale (/s/:id), laid out like the house page: on a phone one column with the actions in a bar at the
  bottom; from lg, the photos and description on the left and a sticky panel on the right (price, the actions, the
  seller). A sold item still opens from a shared link - it says so first. The window scrolls (outside NavComponent).
-->
<div class="min-h-dvh bg-background-light">
  <app-listing-page-header backLink="/sales" backLabel="All items"></app-listing-page-header>

  <main class="mx-auto max-w-5xl px-4 pt-2 pb-32 lg:pb-12">
    <div *ngIf="isLoading" class="animate-pulse space-y-4" aria-busy="true" aria-label="Loading the item">
      <div class="aspect-square rounded-xl bg-gray-200 sm:aspect-[4/3]"></div>
      <div class="space-y-3 rounded-xl bg-white p-5 shadow-card">
        <div class="h-7 w-1/2 rounded bg-gray-200"></div>
        <div class="h-6 w-1/4 rounded bg-gray-200"></div>
        <div class="h-4 w-1/3 rounded bg-gray-200"></div>
      </div>
    </div>

    <div *ngIf="!isLoading && notFound" class="flex flex-col items-center gap-3 py-20 text-center">
      <mat-icon class="!h-12 !w-12 !text-5xl text-primary/30" aria-hidden="true">sell</mat-icon>
      <p class="m-0 text-base font-medium text-gray-900">This item isn't listed any more.</p>
      <p class="m-0 text-sm text-text-secondary">It may have been sold, or removed by the seller.</p>
      <a routerLink="/sales" class="btn-primary mt-2 inline-flex items-center">See what else is for sale</a>
    </div>

    <div *ngIf="!isLoading && loadError" class="flex flex-col items-center gap-3 py-20 text-center">
      <mat-icon class="!h-12 !w-12 !text-5xl text-primary/30" aria-hidden="true">wifi_off</mat-icon>
      <p class="m-0 text-base font-medium text-gray-900">Couldn't load this item.</p>
      <p class="m-0 text-sm text-text-secondary">Check your connection and try again.</p>
      <button type="button" class="btn-primary mt-2 inline-flex items-center gap-2" (click)="loadSale()">
        <mat-icon aria-hidden="true">refresh</mat-icon> Try again
      </button>
    </div>

    <div *ngIf="!isLoading && sale" class="lg:grid lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start lg:gap-6">
      <div class="min-w-0 space-y-4">
        <!-- Sold: still reachable from a shared link, but say so first -->
        <div *ngIf="sale.sold_at" class="flex items-center gap-3 rounded-xl bg-primary p-4 text-white" role="status">
          <mat-icon class="flex-shrink-0 text-accent" aria-hidden="true">sell</mat-icon>
          <div>
            <p class="m-0 font-semibold">This item has been sold.</p>
            <p class="m-0 text-sm text-white/80"><a routerLink="/sales" class="underline">See what else is for sale</a></p>
          </div>
        </div>

        <div class="relative aspect-square overflow-hidden rounded-xl bg-primary/5 shadow-card sm:aspect-[4/3]" [class.grayscale]="sale.sold_at">
          <app-photo-strip [media]="sale.Media" [alt]="sale.item_name" arrows="always" placeholderIcon="image"
            [width]="1000" sizes="(min-width: 1024px) 40rem, 100vw"></app-photo-strip>
          <div *ngIf="condition || sale.is_admin_post" class="pointer-events-none absolute left-3 top-3 flex flex-wrap gap-1.5">
            <span *ngIf="condition" class="rounded-lg bg-white/90 px-2 py-1 text-sm font-medium text-primary shadow-sm">{{ condition }}</span>
            <span *ngIf="sale.is_admin_post" class="rounded-lg bg-white/90 px-2 py-1 text-sm font-medium text-primary shadow-sm">Admin</span>
          </div>
        </div>

        <div class="lg:hidden">
          <ng-container *ngTemplateOutlet="summary; context: { $implicit: false }"></ng-container>
        </div>

        <section *ngIf="sale.text" class="rounded-xl bg-white p-5 shadow-card">
          <h2 class="mb-2 text-lg font-semibold text-primary">About this item</h2>
          <p class="m-0 whitespace-pre-line leading-relaxed text-gray-800">{{ sale.text }}</p>
        </section>

        <div class="lg:hidden">
          <ng-container *ngTemplateOutlet="seller"></ng-container>
        </div>

        <!-- The about page's safety guidelines, where they matter most -->
        <p class="m-0 flex gap-3 rounded-xl border border-primary/10 bg-white/60 p-4 text-sm leading-relaxed text-text-secondary">
          <mat-icon class="flex-shrink-0 text-primary/70" aria-hidden="true">verified_user</mat-icon>
          <span>Meet somewhere public, and check the item before you pay.
            <a routerLink="/about" fragment="disclaimer" class="font-medium text-primary underline">Staying safe</a></span>
        </p>
      </div>

      <aside class="hidden space-y-4 lg:sticky lg:top-24 lg:block">
        <ng-container *ngTemplateOutlet="summary; context: { $implicit: true }"></ng-container>
        <ng-container *ngTemplateOutlet="seller"></ng-container>
      </aside>
    </div>
  </main>

  <div *ngIf="!isLoading && sale"
    class="fixed inset-x-0 bottom-0 z-20 flex items-center gap-2 border-t border-gray-200 bg-white px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:hidden">
    <ng-container *ngTemplateOutlet="actions; context: { $implicit: 'bar' }"></ng-container>
  </div>
</div>

<!-- What, how much, where - and on a wide screen, the actions under it -->
<ng-template #summary let-withActions>
  <section class="rounded-xl bg-white p-5 shadow-card">
    <a *ngIf="category" routerLink="/sales" [queryParams]="{ category: sale!.category }"
      class="mb-2 inline-flex rounded-lg bg-background-light px-2 py-1 text-sm font-medium text-primary hover:bg-primary/10">{{ category }}</a>
    <h1 class="m-0 break-words text-2xl font-bold leading-tight text-primary">{{ sale!.item_name }}</h1>
    <p class="m-0 mt-1 text-sm text-text-secondary">
      <ng-container *ngIf="location">{{ location }} · </ng-container>
      <time [attr.datetime]="sale!.created_at">Posted {{ sale!._age }}</time>
      <span *ngIf="sale!.last_updated_age && sale!.last_updated_age !== sale!._age"> · Updated {{ sale!.last_updated_age }}</span>
    </p>
    <p class="m-0 mt-4 text-2xl font-bold leading-tight text-primary" [class.line-through]="sale!.sold_at" [class.opacity-60]="sale!.sold_at">&#8358;{{ sale!._price }}</p>
    <p *ngIf="sale!.minimum_price && !sale!.sold_at" class="m-0 mt-1 text-sm text-text-secondary">
      Negotiable, down to &#8358;{{ sale!.minimum_price | number }}
    </p>
    <div *ngIf="withActions" class="mt-5 flex flex-wrap items-center gap-2">
      <ng-container *ngTemplateOutlet="actions; context: { $implicit: 'panel' }"></ng-container>
    </div>
  </section>
</ng-template>

<!--
  Chat seller (and Save, Share) for a buyer; Mark as sold for the seller; Sign up when logged out. A sold item only
  shares. `bar` is the phone's bottom bar, `panel` the wide screen's summary.
-->
<ng-template #actions let-place>
  <ng-container *ngIf="!callerService.isLoggedIn">
    <p *ngIf="place === 'bar'" class="m-0 min-w-0 flex-1 truncate text-sm text-text-secondary">Join corpers.ng to chat with {{ sellerDisplayName }}.</p>
    <a routerLink="/signup" [queryParams]="signupQueryParams"
      class="btn-primary inline-flex min-h-11 items-center justify-center !py-0" [class.w-full]="place === 'panel'">Sign up free</a>
  </ng-container>

  <ng-container *ngIf="callerService.isLoggedIn && isOwn">
    <button type="button" (click)="toggleSold()" [disabled]="isUpdatingSold"
      class="inline-flex min-h-11 items-center justify-center rounded-lg border border-primary/30 px-4 font-medium text-primary transition hover:bg-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary disabled:opacity-50"
      [ngClass]="place === 'panel' ? 'w-full' : 'flex-1'">
      {{ sale!.sold_at ? 'Back on sale' : 'Mark as sold' }}
    </button>
    <a routerLink="/dashboard" [queryParams]="{ post: 'sale_' + sale!.id }" class="text-sm font-medium text-primary underline" [class.w-full]="place === 'panel'">Manage on your dashboard</a>
  </ng-container>

  <ng-container *ngIf="callerService.isLoggedIn && !isOwn">
    <button *ngIf="!sale!.sold_at" type="button" (click)="toggleSave()" [disabled]="isSaving" [attr.aria-pressed]="saved"
      [attr.aria-label]="'Save ' + sale!.item_name"
      class="inline-flex min-h-11 flex-shrink-0 items-center justify-center gap-1.5 rounded-lg border border-primary/30 px-3 font-medium text-primary transition hover:bg-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary disabled:opacity-50"
      [class.flex-1]="place === 'panel'">
      <mat-icon class="!size-5 !text-xl !leading-5" aria-hidden="true">{{ saved ? 'bookmark' : 'bookmark_border' }}</mat-icon>
      <span *ngIf="place === 'panel'">{{ saved ? 'Saved' : 'Save' }}</span>
    </button>
    <button type="button" (click)="saleActions.share(sale!)" aria-label="Share this item"
      class="inline-flex min-h-11 flex-shrink-0 items-center justify-center gap-1.5 rounded-lg border border-primary/30 px-3 font-medium text-primary transition hover:bg-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
      [class.flex-1]="place === 'panel'" [class.w-full]="place === 'panel' && sale!.sold_at">
      <mat-icon class="!size-5 !text-xl !leading-5" aria-hidden="true">share</mat-icon>
      <span *ngIf="place === 'panel'">Share</span>
    </button>
    <button *ngIf="!sale!.sold_at" type="button" (click)="saleActions.chatWithSeller(sale!)"
      class="btn-primary inline-flex min-h-11 items-center justify-center gap-2 !py-0"
      [ngClass]="place === 'panel' ? 'order-first w-full' : 'flex-1'">
      <mat-icon class="!size-5 !text-xl !leading-5" aria-hidden="true">chat</mat-icon> Chat seller
    </button>
  </ng-container>
</ng-template>

<ng-template #seller>
  <section class="rounded-xl bg-white p-5 shadow-card">
    <h2 class="mb-3 text-lg font-semibold text-primary">Sold by</h2>
    <div class="flex items-center gap-3">
      <span class="flex size-11 flex-shrink-0 items-center justify-center rounded-full bg-primary font-bold text-accent" aria-hidden="true">{{ sellerInitial }}</span>
      <div class="min-w-0 flex-1">
        <!-- TODO(wachukxs/gh#116): a "Verified corper" label for verified sellers (an NYSC ID card upload, not just an email). -->
        <a [routerLink]="sellerProfileLink" class="font-medium text-gray-900 hover:text-primary hover:underline">{{ sellerDisplayName }}</a>
        <p *ngIf="location" class="m-0 text-sm text-text-secondary">{{ location }}</p>
        <p *ngIf="leaving && !sale!.sold_at" class="m-0 mt-1 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-sm font-medium text-primary">
          <mat-icon class="!size-4 !text-base !leading-4" aria-hidden="true">schedule</mat-icon>{{ leaving }}
        </p>
      </div>
      <a [routerLink]="sellerProfileLink" class="flex-shrink-0 text-sm font-medium text-primary underline">View profile</a>
    </div>
  </section>
</ng-template>
